import './QuestionAudioActions.sass';

import { useEffect } from 'react';
import { Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  deleteSelectedQuestionAudio,
  regenerateSelectedQuestionAudio,
} from '../../actionsSagas';
import {
  getIsRegeneratingQuestionAudio,
  getSelectedQuestion,
} from '../../selectors';

export function QuestionAudioActions({ question }) {
  const dispatch = useDispatch();
  const isRegeneratingAudio = useSelector(getIsRegeneratingQuestionAudio);

  const questionFromStore = useSelector(getSelectedQuestion);
  const selectedQuestion = question || questionFromStore;

  const audio = AudioDecorator.getInstance();

  const handleDeleteAudio = () => {
    audio.pause();
    dispatch(deleteSelectedQuestionAudio(selectedQuestion.id));
  };

  const handlePlayAudio = (e) => {
    e.preventDefault();
    audio.toggle(selectedQuestion.audio.link);
  };

  const handleUpdateAudio = () => {
    if (isRegeneratingAudio) return;

    audio.pause();
    dispatch(regenerateSelectedQuestionAudio(selectedQuestion.id));
  };

  useEffect(() => {
    audio.pause();
    return () => audio.pause();
  }, [selectedQuestion]);

  if (!selectedQuestion?.id) {
    return <></>;
  }

  const hasAudio = Boolean(selectedQuestion.audio);

  return (
    <Row className="question-audio-actions">
      <div className="qa-buttons">
        {hasAudio && (
          <a href={selectedQuestion.audio.link || ''} onClick={handlePlayAudio}>
            <i className="fa fa-volume-up qa-icon sound-icon" />
          </a>
        )}

        {isRegeneratingAudio ? (
          <i
            className="fa fa-circle-notch fa-spin qa-icon update-icon"
            aria-hidden="true"
            title={hasAudio ? 'Regenerating audio' : 'Generating audio'}
          />
        ) : (
          <i
            className="fa fa-refresh qa-icon update-icon"
            aria-hidden="true"
            title={hasAudio ? 'Regenerate audio' : 'Generate audio'}
            onClick={handleUpdateAudio}
          />
        )}

        {hasAudio && (
          <i
            className="fa fa-times qa-icon delete-icon"
            aria-hidden="true"
            onClick={handleDeleteAudio}
          />
        )}
      </div>

      {hasAudio && (
        <Row className="mt-4 text qa-audio-text">
          <p>Audio text:</p>
          <hr />
          <p>
            {selectedQuestion.audio.textSSML
              .replace(/<\/?speak>/g, '')
              .replace(/<\/?prosody[^>]*>/g, '')}
          </p>
        </Row>
      )}
    </Row>
  );
}

/* 
Mix of decorator and singleton patterns: 
- Prevents audios from playing over each other upon 
multiple clicks on the audio icon and calling play().
- Adds toggle() to allow pausing and resuming audios.
- Can be used in multiple components without creating
multiple instances of the Audio class.
- Playing errors don't crash the app.
- Uses a cache-busting technique to prevent caching issues
  with audio files.
References:
- https://refactoring.guru/design-patterns/decorator
- https://refactoring.guru/design-patterns/singleton
*/
export class AudioDecorator {
  // Don't access this property directly, use getInstance()
  static instance;

  #audio;

  #src;

  // Don't instantiate the class directly, use getInstance()
  constructor() {
    this.#audio = new Audio();
  }

  static getInstance() {
    if (!AudioDecorator.instance) {
      AudioDecorator.instance = new AudioDecorator();
    }
    return AudioDecorator.instance;
  }

  play(src) {
    this.pause();
    this.#src = src;
    this.#audio.src = `${src}?t=${Date.now()}`;
    this.#audio.play().catch(() => {});
  }

  pause() {
    this.#audio.pause();
  }

  toggle(src) {
    if (this.#audio.paused || src !== this.#src) {
      this.play(src);
    } else {
      this.pause();
    }
  }
}
